import React, { type ReactElement, useState } from "react";
import { PaymentMethod } from "../../../common/types/enums";
import { WorkflowMode } from "../common/enums";
import {
  Arena,
  DetailedPaymentPrefUnionObjectType,
  RedactedPaymentPref,
  Position,
} from "../common/types";
import { ConsolidatedPaymentPrefContextProvider } from "../Context";
import { type AssociatePaymentPrefMutationTypeEnum } from "../Context/enums";
import { type AccountEdge, type AccountPositions } from "../Context/types";

// TODO Add robust documentation
export interface ConsolidatedPaymentPrefProviderProps {
  /**
   * These are existing payment prefs on the account. Used to display a list to choose from. Required for the create flow
   */
  existingPaymentPrefs?: RedactedPaymentPref[];

  /**
   * Required only for the create flow
   */
  availablePaymentMethods?: Record<PaymentMethod, { isAvailable: boolean; warning: string }>;

  accounts?: AccountEdge[];

  /** Required only on GP flows and when has_position_payment_pref_staff_approvals arena FF is enabled
   */
  accountName?: string;

  /**
   * The arena in context of this workflow.
   */
  arena?: Arena;

  /**
   * Only required for the position flows
   */
  currentPosition?: Position;

  /**
   * The initial mutation (through selection of a enum) to associate payment preferences with target objects.
   * The association mutation can also be set later in the workflow, dynamically.
   */
  initialAssociatePaymentPref?: AssociatePaymentPrefMutationTypeEnum;

  /**
   * The initial payment method selected for an existing payment preference.
   * Only used when the existing payment preference is being edited.
   */
  initialSelectedPaymentMethod?: PaymentMethod;

  /**
   * Initial data for editing an existing payment preference.
   * Only used when the existing payment preference is being edited.
   */
  initialPaymentPrefId?: string;

  /**
   * The source payment pref id for a switch operation
   */
  sourcePaymentPrefId?: string;

  children: ReactElement;
  ListOfPositionsToUpdateComponent?: React.FC | null;

  /**
   * The current mode in which the workflow is operating.
   */
  workflowMode: WorkflowMode;

  /**
   * Initial Header component to render on update payment method page
   */
  initialSelectAvailablePaymentPrefsFormHeaderComponent?: React.FC | null;

  /**
   * The initial payment pref detail to display. Only used during payee workflow
   */
  initialPaymentPrefDetail?: DetailedPaymentPrefUnionObjectType;
}

export const ConsolidatedPaymentPrefProvider = ({
  existingPaymentPrefs,
  availablePaymentMethods,
  accounts,
  currentPosition,
  ListOfPositionsToUpdateComponent: DefaultListOfPositionsToUpdateComponent = null,
  initialAssociatePaymentPref,
  initialSelectedPaymentMethod,
  initialPaymentPrefId,
  sourcePaymentPrefId,
  workflowMode,
  initialSelectAvailablePaymentPrefsFormHeaderComponent = null,
  accountName,
  arena,
  initialPaymentPrefDetail,
  children,
}: ConsolidatedPaymentPrefProviderProps) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | undefined>(
    initialSelectedPaymentMethod,
  );
  const [paymentPrefDetail, setPaymentPrefDetail] = useState<
    DetailedPaymentPrefUnionObjectType | undefined
  >(initialPaymentPrefDetail);
  const [selectedPaymentPref, setSelectedPaymentPref] = useState<
    RedactedPaymentPref | undefined
  >();
  const [isExistingPaymentPref, setIsExistingPaymentPref] = useState<boolean>(false);
  const [paymentPrefId, setPaymentPrefId] = useState<string | undefined>(initialPaymentPrefId);
  const [updatedPositionsPerAccount, setUpdatedPositionsPerAccount] = useState<
    AccountPositions | undefined
  >();
  const [associatePaymentPref, setAssociatePaymentPref] = useState<
    AssociatePaymentPrefMutationTypeEnum | undefined
  >(initialAssociatePaymentPref);

  const [
    SelectAvailablePaymentPrefsFormHeaderComponent,
    setSelectAvailablePaymentPrefsFormHeaderComponent,
  ] = useState<React.FC | null>(() => initialSelectAvailablePaymentPrefsFormHeaderComponent);
  const [ListOfPositionsToUpdateComponent, setListOfPositionsToUpdateComponent] =
    useState<React.FC | null>(() => DefaultListOfPositionsToUpdateComponent);

  return (
    <ConsolidatedPaymentPrefContextProvider
      value={{
        associatePaymentPref,
        setAssociatePaymentPref,
        SelectAvailablePaymentPrefsFormHeaderComponent,
        setSelectAvailablePaymentPrefsFormHeaderComponent,
        ListOfPositionsToUpdateComponent,
        setListOfPositionsToUpdateComponent,
        existingPaymentPrefs,
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        paymentPrefDetail,
        setPaymentPrefDetail,
        selectedPaymentPref,
        setSelectedPaymentPref,
        isExistingPaymentPref,
        setIsExistingPaymentPref,
        sourcePaymentPrefId,
        paymentPrefId,
        setPaymentPrefId,
        updatedPositionsPerAccount,
        setUpdatedPositionsPerAccount,
        currentPosition,
        accounts,
        paymentMethods: availablePaymentMethods,
        workflowMode,
        accountName,
        arena,
        initialPaymentPrefDetail,
      }}
    >
      {children}
    </ConsolidatedPaymentPrefContextProvider>
  );
};
